/**
 * Project sync: translates Mutative patches ↔ Convex mutations.
 *
 * Two directions:
 * 1. Local → Convex: groupPatchesByTable() converts patches to Convex-friendly format,
 *    then dispatched via a single Convex mutation.
 * 2. Convex → Local: subscription data converted to ProjectState and applied to store.
 */

import type { Patch } from 'mutative'
import type { ProjectState, ClipState, WorkflowState } from './projectState'

// ── Patch → Convex table mapping ──────────────────────────

interface TablePatch {
  table: 'workflows' | 'workflowClips'
  docId: string
  fields: Record<string, unknown>
}

/**
 * Convert Mutative patches (path-based) into grouped Convex table patches.
 *
 * Path shapes:
 *   ["workflow", "narrative"]             → workflows[workflowId].narrative
 *   ["workflow", "audioTracks", 0, "volume"] → full audioTracks array replace
 *   ["clips", "abc123", "trimStart"]     → workflowClips[abc123].trimStart
 *   ["clipOrder", ...]                   → reorder (handled via orderIndex patches on clips)
 */
export function patchesToConvex(
  patches: Patch[],
  workflowId: string,
): TablePatch[] {
  const result: TablePatch[] = []
  // Accumulate workflow-level fields
  const workflowFields: Record<string, unknown> = {}
  // Accumulate clip-level fields per clip
  const clipFields: Record<string, Record<string, unknown>> = {}

  for (const patch of patches) {
    const path = patch.path as (string | number)[]
    if (path.length < 2) continue

    const root = path[0]

    if (root === 'workflow') {
      const field = String(path[1])
      if (path.length === 2) {
        // Direct field: workflow.narrative, workflow.title, etc.
        workflowFields[field] = patch.value
      } else {
        // Nested: workflow.audioTracks[0].volume → replace full array
        // For arrays (audioTracks, textOverlays, characters), we need to
        // collect the final state from the store and send the full array.
        // Individual element patches are collapsed by the caller.
        workflowFields[field] = patch.value
      }
    } else if (root === 'clips') {
      const clipId = String(path[1])
      if (path.length >= 3) {
        const field = String(path[2])
        clipFields[clipId] ??= {}
        if (path.length === 3) {
          clipFields[clipId][field] = patch.value
        } else {
          // Nested clip field (effects.kenBurns.startScale etc)
          // These need full-field replacement from the store
          clipFields[clipId][field] = patch.value
        }
      }
    } else if (root === 'clipOrder') {
      // clipOrder changes are reflected via orderIndex on individual clips,
      // which are already captured in the clips patches
    }
  }

  if (Object.keys(workflowFields).length > 0) {
    result.push({ table: 'workflows', docId: workflowId, fields: workflowFields })
  }

  for (const [clipId, fields] of Object.entries(clipFields)) {
    result.push({ table: 'workflowClips', docId: clipId, fields })
  }

  return result
}

// ── Convex data → ProjectState ────────────────────────────

/**
 * Build a ProjectState from raw Convex query results.
 * Used for initial hydration and server sync.
 */
export function convexToProjectState(
  workflow: any,
  clips: any[],
): ProjectState {
  const sortedClips = [...clips]
    .filter((c) => !c.deletedAt)
    .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))

  const clipsMap: Record<string, ClipState> = {}
  for (const c of clips) {
    clipsMap[c._id] = {
      _id: c._id,
      workflowId: c.workflowId,
      orderIndex: c.orderIndex,
      title: c.title,
      description: c.description,
      videoPrompt: c.videoPrompt,
      promptSummary: c.promptSummary,
      charactersInClip: c.charactersInClip,
      linkGroupId: c.linkGroupId,
      duration: c.duration,
      trimStart: c.trimStart,
      trimEnd: c.trimEnd,
      speed: c.speed,
      timelineStartTime: c.timelineStartTime,
      storyboardImageUrl: c.storyboardImageUrl,
      videoUrl: c.videoUrl,
      videoStatus: c.videoStatus,
      firstFrameUrl: c.firstFrameUrl,
      lastFrameUrl: c.lastFrameUrl,
      isJumpCut: c.isJumpCut,
      sceneLabel: c.sceneLabel,
      generationMode: c.generationMode,
      solidColor: c.solidColor,
      dialog: c.dialog,
      transition: c.transition,
      effects: c.effects,
      metadata: c.metadata,
      transcription: c.transcription,
      transcriptionStatus: c.transcriptionStatus,
      deletedAt: c.deletedAt,
      createdAt: c.createdAt,
      updatedAt: c.updatedAt,
      videoVersionCount: c.videoVersionCount,
      startKeyframeUrl: c.startKeyframeUrl,
      startKeyframeSource: c.startKeyframeSource,
      endKeyframeUrl: c.endKeyframeUrl,
      analysis: c.analysis,
      analysisStatus: c.analysisStatus,
      staleReasons: c.staleReasons,
      keyframes: c.keyframes,
      keyframeStatus: c.keyframeStatus,
      activeVideoId: c.activeVideoId,
      spatialX: c.spatialX,
      spatialY: c.spatialY,
      spatialWidth: c.spatialWidth,
      spatialHeight: c.spatialHeight,
      spatialRotation: c.spatialRotation,
      spatialZIndex: c.spatialZIndex,
      spatialOpacity: c.spatialOpacity,
      trackIndex: c.trackIndex,
    }
  }

  const clipOrder = sortedClips.map((c) => c._id)

  const ws: WorkflowState = {
    _id: workflow._id,
    title: workflow.title,
    narrative: workflow.narrative,
    characters: workflow.characters || [],
    globalContext: workflow.globalContext,
    settings: workflow.settings || {},
    audioTracks: workflow.audioTracks || [],
    textOverlays: workflow.textOverlays || [],
    timelineMode: workflow.timelineMode,
    exportDuration: workflow.exportDuration,
  }

  return { workflow: ws, clips: clipsMap, clipOrder }
}

// ── Full-array field resolution ───────────────────────────

/**
 * For workflow array fields (audioTracks, textOverlays, characters),
 * Mutative may generate per-element patches. Convex needs the full array.
 * This function resolves patches that touch array elements into a single
 * patch containing the full array from the current state.
 */
export function resolveArrayPatches(
  patches: Patch[],
  currentState: ProjectState,
): Patch[] {
  const resolved: Patch[] = []
  const handledArrayFields = new Set<string>()

  for (const patch of patches) {
    const path = patch.path as (string | number)[]
    if (path[0] === 'workflow' && path.length > 2) {
      const field = String(path[1])
      const arrayFields = ['audioTracks', 'textOverlays', 'characters', 'settings']
      if (arrayFields.includes(field) && !handledArrayFields.has(field)) {
        handledArrayFields.add(field)
        // Replace with full array from current state
        resolved.push({
          op: 'replace' as const,
          path: ['workflow', field],
          value: (currentState.workflow as any)[field],
        })
        continue
      } else if (handledArrayFields.has(field)) {
        continue // Skip, already handled
      }
    }

    if (path[0] === 'clips' && path.length > 3) {
      const clipId = String(path[1])
      const field = String(path[2])
      const key = `${clipId}.${field}`
      if (!handledArrayFields.has(key)) {
        handledArrayFields.add(key)
        resolved.push({
          op: 'replace' as const,
          path: ['clips', clipId, field],
          value: (currentState.clips[clipId] as any)?.[field],
        })
        continue
      } else {
        continue
      }
    }

    resolved.push(patch)
  }

  return resolved
}
